
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AuthCard } from '@/components/auth';
import { useToast } from '@/hooks/use-toast';

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Helper to check if we're on the callback route
  const isCallback = location.pathname.includes('/callback');

  // Parse query parameters from URL
  const getQueryParam = (param: string): string | null => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(param);
  };

  // Log domain information for debugging purposes
  useEffect(() => {
    const currentDomain = window.location.origin;
    console.log("Current domain:", currentDomain);
    console.log("Current pathname:", window.location.pathname);
    console.log("Current URL:", window.location.href);
    console.log("Domain verification file should be accessible at:", `${currentDomain}/.well-known/microsoft-identity-association.json`);
    
    // Check if we can access the verification file
    fetch(`${currentDomain}/.well-known/microsoft-identity-association.json`)
      .then(response => {
        if (response.ok) {
          console.log("✅ Domain verification file is accessible!");
          return response.json();
        } else {
          console.error("❌ Domain verification file is NOT accessible:", response.status);
          throw new Error(`Verification file not accessible: ${response.status}`);
        }
      })
      .then(data => {
        console.log("Verification file content:", data);
      })
      .catch(err => {
        console.error("Error accessing verification file:", err);
      });
  }, []);

  // Check if there's an error in the URL (from Microsoft redirect)
  useEffect(() => {
    const errorParam = getQueryParam('error');
    const errorDescription = getQueryParam('error_description');
    
    if (errorParam && errorDescription) {
      console.error("Auth error from provider:", errorParam, errorDescription);
      const decodedError = decodeURIComponent(errorDescription);
      setError(decodedError);
      toast({
        title: "Authentication Error",
        description: `${errorParam}: ${decodedError}`,
        variant: "destructive",
      });
    }
  }, [location, toast]);

  // Handle authentication session
  useEffect(() => {
    let mounted = true;
    
    // Function to handle successful sessions
    const handleSuccessfulSession = (session: any) => {
      if (!mounted) return;
      
      if (session) {
        console.log("User authenticated:", session.user?.email || session.user?.id);
        // Print user details for debugging
        console.log("User details:", session.user);
        console.log("Authentication provider:", session.user?.app_metadata?.provider);
        console.log("User ID:", session.user?.id);
        console.log("Email:", session.user?.email);
        
        toast({
          title: "Sign in successful",
          description: "You have been signed in successfully.",
        });
        navigate('/');
      } else if (isCallback) {
        // If we're on the callback route but don't have a session, show more detailed error
        console.log("No session on callback route, checking for errors");
        console.log("URL hash:", window.location.hash);
        console.log("URL search:", window.location.search);
        
        if (!error) {
          setError("Failed to complete authentication. Please try again.");
        }
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed in Auth component:", event, session?.user?.email);
        console.log("Full event details:", { event, session });
        
        if (event === 'SIGNED_IN') {
          handleSuccessfulSession(session);
        } else if (event === 'SIGNED_OUT') {
          console.log("User signed out");
        } else if (event === 'TOKEN_REFRESHED') {
          console.log("Token refreshed");
        } else if (event === 'USER_UPDATED') {
          console.log("User updated");
        }
      }
    );

    // Process initial session
    const processInitialSession = async () => {
      try {
        console.log("Checking for existing session...");
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Session error:", error.message);
          setError(error.message);
          return;
        }
        
        console.log("Initial session check in Auth page:", data.session);
        handleSuccessfulSession(data.session);
      } catch (error: any) {
        console.error("Error getting session:", error.message);
      }
    };

    // Run initial session check
    processInitialSession();

    // Cleanup
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, toast, isCallback, error]);

  // If we're on the callback route, show a loading state
  if (isCallback) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Completing authentication...</p>
          <p className="text-sm text-muted-foreground mt-2">Callback URL: {window.location.href}</p>
          {error && (
            <div className="mt-4 p-2 bg-red-50 text-red-600 rounded">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <AuthCard 
        loading={loading}
        error={error}
        setLoading={setLoading}
        setError={setError}
      />
    </div>
  );
};

export default Auth;
